import type { Feature, Polygon } from 'geojson';
import { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

import useMapContext from 'components/Map/hooks/useMapContext';

import {
    MAP_LAYER,
    PRESCRIPTION_NORMALIZED_RATE_PROPERTY,
    PRESCRIPTION_RATE_PROPERTY
} from './constants';
import type {
    ApiRateBounds,
    UsePrescriptionZonesParams,
    UsePrescriptionZonesReturn
} from './types';

// Normalized midpoint used when rate bounds collapse to a single value
// (min === max). Midpoint keeps the color in the medium bucket rather than
// biasing low/high when the prescription is flat.
const FLAT_BOUNDS_NORMALIZED_VALUE = 0.5;

const computeNormalizedRate = (rate: number, bounds: ApiRateBounds): number => {
    const { max, min } = bounds;
    const span = max - min;

    if (span <= 0) {
        return FLAT_BOUNDS_NORMALIZED_VALUE;
    }

    const normalized = (rate - min) / span;

    if (normalized < 0) {
        return 0;
    }

    if (normalized > 1) {
        return 1;
    }

    return normalized;
};

// Imperative Mapbox-feature-store hook for rendering variable-rate prescription
// zones onto the planning map. Mirrors the lifecycle pattern used by
// useFlightPaths / useStaticGeometry: add features via useMapContext().features
// on data change, remove them on data clear or unmount.
//
// Rendering contract: the MAP_LAYER.PRESCRIPTION_ZONES_FILL paint style
// (registered in useLayerStyles) interpolates fill-color over a pre-computed
// `normalizedRate` feature property in [0, 1]. We do the normalization here
// so the paint expression stays bounds-agnostic and doesn't require a
// runtime setPaintProperty call when bounds shift between sessions.
const usePrescriptionZones = ({
    bounds,
    zones
}: UsePrescriptionZonesParams): UsePrescriptionZonesReturn => {
    const { features } = useMapContext();
    const { addFeature, removeFeature } = features;

    const addedFeatureIdsRef = useRef<string[]>([]);

    useEffect(() => {
        // Clear any previously-rendered zone features before deciding what to
        // add — cheapest full-reconcile strategy, matches useFlightPaths'
        // clearHighlight idiom.
        for (const featureId of addedFeatureIdsRef.current) {
            removeFeature(featureId);
        }

        addedFeatureIdsRef.current = [];

        if (!zones || zones.length === 0 || !bounds) {
            return;
        }

        const addedIds: string[] = [];

        for (const zone of zones) {
            const polygonFeature: Feature<Polygon> = {
                geometry: {
                    coordinates: zone.coordinates,
                    type: 'Polygon'
                },
                id: uuidv4(),
                properties: {
                    [PRESCRIPTION_NORMALIZED_RATE_PROPERTY]: computeNormalizedRate(
                        zone.rate,
                        bounds
                    ),
                    [PRESCRIPTION_RATE_PROPERTY]: zone.rate
                },
                type: 'Feature'
            };

            const added = addFeature({
                feature: polygonFeature,
                layerName: MAP_LAYER.PRESCRIPTION_ZONES_FILL
            });

            const addedId = added.id as string | undefined;

            if (addedId) {
                addedIds.push(addedId);
            }
        }

        addedFeatureIdsRef.current = addedIds;
    }, [zones, bounds, addFeature, removeFeature]);

    useEffect(
        () => () => {
            for (const featureId of addedFeatureIdsRef.current) {
                removeFeature(featureId);
            }

            addedFeatureIdsRef.current = [];
        },
        [removeFeature]
    );

    return {
        featureIds: addedFeatureIdsRef.current,
        // Layer + paint style are registered statically in useLayerStyles at
        // MapBox mount; consumers can treat downstream rendering as always
        // wired once this hook mounts.
        layerReady: true
    };
};

export default usePrescriptionZones;

export type { UsePrescriptionZonesParams, UsePrescriptionZonesReturn } from './types';
